import React, { useState, FormEvent, ChangeEvent, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import QRCode from 'qrcode';

interface FormData {
  expert_name: string;
  twitter_handle: string;
  nostr_npub: string;
  prediction_type: 'target' | 'range' | 'pricetimerange' | 'top' | 'bottom' | 'sentiment';
  price_target?: string;
  price_min?: string;
  price_max?: string;
  target_time: string;
  source_url?: string;
  notes?: string;
  submitter_email?: string;
}

interface PaymentData {
  amount: number;
  bolt11: string;
  payment_hash: string;
  checking_id: string;
  qr_code?: string;
}

interface Theme {
  surface: string;
  text: string;
  primary: string;
  background: string;
  border: string;
  subText: string;
}

interface SubmitPredictionFormProps {
  isOpen?: boolean;
  onClose?: () => void;
  isDarkMode?: boolean;
  theme?: Theme;
  onPreviewGenerated?: (extractionResult: any) => void;
  onClearPreview?: () => void;
}

// Dynamic and compact form styles
const formStyles = {
  container: {
    width: '100%',
    minWidth: '260px',
    maxWidth: '320px',
    margin: '0',
    padding: '12px',
    borderRadius: '12px',
    height: 'calc(100vh - 300px)',
    minHeight: '600px',
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto' as const,
    boxSizing: 'border-box' as const,
  },
  formGroup: {
    marginBottom: '10px',
  },
  formRow: {
    display: 'flex',
    gap: '8px',
  },
  label: {
    display: 'block',
    marginBottom: '4px',
    fontWeight: '600',
    fontSize: '14px',
  },
  input: {
    width: '100%',
    padding: '5px 6px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '4px',
    fontSize: '13px',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
    marginBottom: '4px',
  },
  radioLabel: {
    fontWeight: 'normal',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    padding: '4px 6px',
    margin: '1px 0',
    borderRadius: '4px',
    border: '1px solid transparent',
    transition: 'all 0.2s ease',
    fontSize: '13px',
  },
  radioInput: {
    width: '16px',
    minWidth: '16px',
    minHeight: '16px',
    height: '16px',
    margin: '0 8px 0 0',
    border: '2px solid #007acc',
    borderRadius: '50%',
    background: 'white',
    cursor: 'pointer',
    position: 'relative',
    flexShrink: 0,
  } as React.CSSProperties,
  small: {
    display: 'block',
    marginTop: '3px',
    fontSize: '12px',
    lineHeight: '1.3',
  },
  error: {
    color: '#e53e3e',
    marginTop: '3px',
    fontSize: '12px',
  },
  submitButton: {
    padding: '8px 12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    width: '100%',
  },
  successMessage: {
    textAlign: 'center' as const,
    padding: '16px',
  }
};

const SubmitPredictionForm: React.FC<SubmitPredictionFormProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  theme: propTheme,
  onPreviewGenerated,
  onClearPreview
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine current asset from route
  const currentAsset = React.useMemo(() => {
    if (location.pathname.includes('/mstr')) return 'mstr';
    if (location.pathname.includes('/naka')) return 'naka';
    return 'btc'; // default
  }, [location.pathname]);

  // Asset-specific configuration
  const assetInfo = {
    btc: { 
      name: 'Bitcoin', 
      symbol: 'BTC',
      apiEndpoint: '/api/submissions',
      examples: {
        target: '$100,000',
        range: '$80,000 - $120,000'
      }
    },
    mstr: { 
      name: 'Strategy₿', 
      symbol: 'MSTR',
      apiEndpoint: '/api/mstr/submissions',
      examples: {
        target: '$500',
        range: '$400 - $600'
      }
    },
    naka: { 
      name: 'Nakamoto', 
      symbol: 'NAKA',
      apiEndpoint: '/api/naka/predictions',
      examples: {
        target: '$10',
        range: '$8 - $12'
      }
    }
  };

  const currentAssetInfo = assetInfo[currentAsset];
  
  const contextTheme = useContext(ThemeContext);
  const theme = propTheme || contextTheme || {
    surface: '#ffffff',
    text: '#333333',
    primary: '#3182ce',
    background: '#f7f7f7',
    border: '#dddddd',
    subText: '#666666',
  };
  
  // State for success screen contact details
  const [successContactDetails, setSuccessContactDetails] = useState<string>('');
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);

  // Handle contact details submission on success screen
  const handleContactSubmission = async () => {
    if (!successContactDetails.trim()) {
      handleCloseModal();
      return;
    }

    setIsSubmittingContact(true);
    try {
      // You can add API call here to store contact details for notifications
      setContactSubmitted(true);
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error) {
      console.error('Error submitting contact details:', error);
      handleCloseModal();
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Payment state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<number>(1); // Default 1 sat
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid' | 'expired'>('pending');
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  
  // Helper function to get default prediction date (3 months from now)
  const getDefaultPredictionDate = () => {
    const now = new Date();
    const threeMonthsLater = new Date(now.getFullYear(), now.getMonth() + 3, now.getDate());
    return threeMonthsLater.toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState<FormData>({
    expert_name: '',
    twitter_handle: '',
    nostr_npub: '',
    prediction_type: 'target',
    price_target: '',
    target_time: getDefaultPredictionDate(),
    source_url: '',
    notes: '',
    submitter_email: ''
  });

  // Unified prediction input state
  const [predictionInput, setPredictionInput] = useState<string>('');
  const [isExtracting, setIsExtracting] = useState<boolean>(false);
  const [extractionResult, setExtractionResult] = useState<any>(null);
  const [predictionInputError, setPredictionInputError] = useState<string>('');
  const [hasSuccessfulExtraction, setHasSuccessfulExtraction] = useState<boolean>(false);
  const [isFormExpanded, setIsFormExpanded] = useState<boolean>(false);
  
  // Handle input changes
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
    // Note: Preview update now handled by useEffect below
  };
  
  // Handle radio button changes
  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      prediction_type: e.target.value as 'target' | 'range' | 'pricetimerange' | 'top' | 'bottom' | 'sentiment'
    });
    // Note: Preview update now handled by useEffect below
  };
  
  // useEffect to update preview when relevant form fields change
  // This ensures we use the latest state values instead of stale closure values
  useEffect(() => {
    if (onPreviewGenerated && (isFormExpanded || hasSuccessfulExtraction)) {
      updatePreviewFromForm();
    }
  }, [formData.expert_name, formData.twitter_handle, formData.price_target, formData.price_min, formData.price_max, formData.target_time, formData.prediction_type, isFormExpanded, hasSuccessfulExtraction]);

  // Handle prediction input changes
  const handlePredictionInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setPredictionInput(e.target.value);
    setPredictionInputError('');
    // Clear previous extraction result when input changes
    if (extractionResult) {
      setExtractionResult(null);
      setHasSuccessfulExtraction(false);
    }
    // Clear preview when input changes
    if (onClearPreview) {
      onClearPreview();
    }
  };

  // Helper function to create prediction text based on form data
  const createPredictionText = (data: FormData): string => {
    const formatNumber = (num: string | number | undefined): string => {
      if (!num && num !== 0) return '';
      
      // Convert to string if it's a number
      const numStr = typeof num === 'number' ? num.toString() : String(num);
      
      // Check if it's a valid string before calling replace
      if (typeof numStr !== 'string') return '';
      
      const parsed = parseFloat(numStr.replace(/[,$]/g, ''));
      return isNaN(parsed) ? '' : `$${parsed.toLocaleString()}`;
    };

    switch (data.prediction_type) {
      case 'target':
        return data.price_target ? formatNumber(data.price_target) : '';
      case 'range':
        if (data.price_min && data.price_max) {
          return `${formatNumber(data.price_min)} - ${formatNumber(data.price_max)}`;
        }
        return '';
      case 'pricetimerange':
        return data.price_min ? `Above ${formatNumber(data.price_min)}` : '';
      case 'top':
        return data.price_target ? `TOP: ${formatNumber(data.price_target)}` : '';
      case 'bottom':
        return data.price_target ? `BOTTOM: ${formatNumber(data.price_target)}` : '';

      case 'sentiment':
        // For sentiment, return the notes/comment text or a placeholder
        return data.notes ? data.notes : 'Sentiment prediction';
      default:
        return '';
    }
  };

  // Update preview based on current form data
  const updatePreviewFromForm = () => {
    if (onPreviewGenerated && (isFormExpanded || hasSuccessfulExtraction)) {
      // Helper function to safely convert price string to number
      const parsePrice = (priceStr: string | undefined | null | any): number | undefined => {
        if (!priceStr || typeof priceStr !== 'string' || priceStr.trim() === '') return undefined;
        const cleanedPrice = priceStr.replace(/[,$]/g, ''); // Remove commas and dollar signs
        const parsedPrice = parseFloat(cleanedPrice);

        return isNaN(parsedPrice) ? undefined : parsedPrice;
      };

      // Create preview data from current form values with proper number conversion
      const parsedPriceTarget = parsePrice(formData.price_target);
      const parsedPriceMin = parsePrice(formData.price_min);
      const parsedPriceMax = parsePrice(formData.price_max);
      
      // Map price_target to the correct field based on prediction type
      // top uses price_max, bottom uses price_min, others use price_target directly
      const previewData = {
        success: true,
        data: {
          expert_name: formData.expert_name,
          twitter_handle: formData.twitter_handle,
          prediction_type: formData.prediction_type,
          // Map prices based on prediction type
          price_target: formData.prediction_type === 'sentiment' ? undefined : 
                       formData.prediction_type === 'top' ? parsedPriceTarget :
                       formData.prediction_type === 'bottom' ? parsedPriceTarget :
                       parsedPriceTarget,
          price_min: formData.prediction_type === 'sentiment' ? undefined :
                    formData.prediction_type === 'bottom' ? parsedPriceTarget :
                    parsedPriceMin,
          price_max: formData.prediction_type === 'sentiment' ? undefined :
                    formData.prediction_type === 'top' ? parsedPriceTarget :
                    parsedPriceMax,
          target_time: formData.target_time,
          source_tweet_text: formData.notes,
          // Add prediction text for better preview display
          price_prediction: createPredictionText(formData),
          // Preserve profile image URL from original extraction result
          profile_image_url: extractionResult?.data?.profile_image_url || undefined
        }
      };
      
      // Only update preview if we have minimum required data for each prediction type
      const hasRequiredData = formData.expert_name && formData.target_time && (() => {
        switch (formData.prediction_type) {
          case 'sentiment':
            return true; // Only needs expert name and time
          case 'target':
            return !!formData.price_target;
          case 'range':
            return !!(formData.price_min && formData.price_max);
          case 'pricetimerange':
            return !!formData.price_min;
          case 'top':
            return !!formData.price_target;
          case 'bottom':
            return !!formData.price_target;

          default:
            return !!(formData.price_target || formData.price_min || formData.price_max);
        }
      })();
      
      // Always show tweet if we have extracted tweet data, regardless of prediction validation
      const hasExtractedTweetData = extractionResult && extractionResult.data;
      
      if (hasRequiredData || hasExtractedTweetData) {
        onPreviewGenerated(previewData);
      }
    }
  };

  // Handle Grok extraction and preview
  const handleExtractPreview = async () => {
    if (!predictionInput.trim()) {
      setPredictionInputError('Please enter a tweet URL or text');
      return;
    }

    setIsExtracting(true);
    setPredictionInputError('');

    try {
      
      const response = await fetch('/api/preview-prediction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          input: predictionInput,
          expertName: formData.expert_name || undefined,
          twitterHandle: formData.twitter_handle || undefined
        })
      });

      const result = await response.json();
      
      setExtractionResult(result);

      if (result.success && result.data) {
        // Mark extraction as successful
        setHasSuccessfulExtraction(true);
        
        // Auto-fill form with extracted data
        setFormData(prev => ({
          ...prev,
          expert_name: result.data.expert_name || prev.expert_name,
          twitter_handle: result.data.twitter_handle || prev.twitter_handle,
          prediction_type: result.data.prediction_type || prev.prediction_type,
          price_target: result.data.price_prediction || prev.price_target,
          price_min: result.data.price_min || prev.price_min,
          price_max: result.data.price_max || prev.price_max,
          target_time: result.data.target_time || prev.target_time,
          source_url: result.data.tweet_url || predictionInput,
          notes: result.data.source_tweet_text || result.data.notes || prev.notes
        }));

        
        // Generate live chart preview
        if (onPreviewGenerated) {
          onPreviewGenerated(result);
        }
      } else if (result.rate_limited || result.cost_info?.x_api_rate_limited) {
        // Handle rate limiting with professional message
        if (result.saved_for_later) {
          setPredictionInputError('Rate limits reached. Your link was saved and we will process it automatically.');
        } else {
          setPredictionInputError('Rate limits active. Try again in 15 minutes.');
        }
      } else {
        // Generic error without exposing system details
        setPredictionInputError('Extraction failed. Please fill the form manually.');
      }

    } catch (error) {
      console.error('❌ Extraction error:', error);
      setPredictionInputError('Network error. Please try again.');
      setExtractionResult({
        success: false,
        error: 'Network error. Please try again.'
      });
    } finally {
      setIsExtracting(false);
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.expert_name) {
      errors.expert_name = 'Please enter the name of the person who made this prediction';
    }
    
    // Price validation - not required for sentiment tweets
    if (formData.prediction_type !== 'sentiment') {
      if (formData.prediction_type === 'target' && !formData.price_target) {
        errors.price_target = 'Please enter the price target';
      }
      
      if (formData.prediction_type === 'range') {
        if (!formData.price_min) {
          errors.price_min = 'Please enter the minimum price';
        }
        if (!formData.price_max) {
          errors.price_max = 'Please enter the maximum price';
        }
      }
      
      if (formData.prediction_type === 'pricetimerange' && !formData.price_min) {
        errors.price_min = 'Please enter the minimum price';
      }
      
      if (formData.prediction_type === 'top' && !formData.price_target) {
        errors.price_target = 'Please enter the price ceiling/top';
      }
      
      if (formData.prediction_type === 'bottom' && !formData.price_target) {
        errors.price_target = 'Please enter the price floor/bottom';
      }
    }
    
    if (!formData.target_time) {
      errors.target_time = 'Please enter when this price is predicted to be reached';
    }
    
    if (formData.submitter_email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.submitter_email)) {
      errors.submitter_email = 'Please enter a valid email address';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission - now shows payment modal
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Show payment modal instead of submitting directly
    setShowPaymentModal(true);
  };
  
  const handleNewSubmission = () => {
    setSubmissionSuccess(false);
  };

  const handleCloseModal = () => {
    // Clear any preview when modal closes
    if (onClearPreview) {
      onClearPreview();
    }
    
    if (onClose) {
      onClose();
    } else {
      setSubmissionSuccess(false);
    }
    
    // Navigate back to the appropriate chart page based on current asset
    if (currentAsset === 'mstr') {
      navigate('/mstr');
    } else if (currentAsset === 'naka') {
      navigate('/naka');
    } else {
      navigate('/btc');
    }
  };
  
  // Show success screen if submission was successful
  if (submissionSuccess) {
    return (
      <div style={{
        ...formStyles.container,
        backgroundColor: theme.surface,
        color: theme.text
      }}>
        <div style={formStyles.successMessage}>
          <h2 style={{ color: theme.primary }}>✅ Submission Successful!</h2>
          <p style={{ color: theme.text, marginBottom: '30px' }}>
            Your {currentAssetInfo.name} price prediction has been submitted successfully and will be reviewed by our team.
          </p>
          
          {!contactSubmitted ? (
            <>
              <div style={{ marginBottom: '20px', textAlign: 'left', maxWidth: '400px', margin: '0 auto 20px auto' }}>
                <h3 style={{ color: theme.text, fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>
                  📬 Stay in the loop!
                </h3>
                <p style={{ color: theme.subText, fontSize: '14px', marginBottom: '15px', textAlign: 'center' }}>
                  Wanna get notified about changes or updates on your submission? Leave your e-mail or LNURL here to get notified!
                </p>
                <input 
                  type="text"
                  value={successContactDetails}
                  onChange={(e) => setSuccessContactDetails(e.target.value)}
                  placeholder="your@email.com or your@lnurl.com"
                  style={{
                    ...formStyles.input,
                    backgroundColor: theme.background,
                    color: theme.text,
                    borderColor: theme.border,
                    marginBottom: '15px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button 
                  style={{ 
                    ...formStyles.submitButton,
                    backgroundColor: 'transparent',
                    color: theme.subText,
                    border: `1px solid ${theme.border}`,
                    width: 'auto',
                    padding: '10px 20px',
                    fontSize: '14px'
                  }}
                  onClick={handleCloseModal}
                >
                  Skip
                </button>
                <button 
                  style={{ 
                    ...formStyles.submitButton,
                    backgroundColor: theme.primary,
                    color: 'white',
                    width: 'auto',
                    padding: '10px 20px',
                    fontSize: '14px',
                    opacity: isSubmittingContact ? 0.7 : 1
                  }}
                  onClick={handleContactSubmission}
                  disabled={isSubmittingContact}
                >
                  {isSubmittingContact ? 'Saving...' : 'Notify Me!'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p style={{ color: theme.primary, marginBottom: '20px', fontSize: '14px' }}>
                🎉 Thanks! We'll keep you posted!
              </p>
              <button 
                style={{ 
                  ...formStyles.submitButton,
                  backgroundColor: theme.primary,
                  color: 'white',
                  width: 'auto',
                  padding: '12px 30px',
                  margin: '0 auto',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
                onClick={handleCloseModal}
              >
                Done!
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
  
  // Add a style block for radio buttons and mobile input fixes
  const radioAndMobileStyles = `
    /* Global radio button styling - no :has() for better compatibility */
    input[type='radio'] {
      position: relative !important;
      width: 18px !important;
      height: 18px !important;
      border: 2px solid #007acc !important;
      border-radius: 50% !important;
      background: white !important;
      appearance: none !important;
      -webkit-appearance: none !important;
      -moz-appearance: none !important;
      margin: 0 8px 0 0 !important;
      cursor: pointer !important;
    }
    
    input[type='radio']:checked {
      background: #007acc !important;
      border-color: #007acc !important;
      box-shadow: inset 0 0 0 3px white !important;
    }
    
    input[type='radio']:focus {
      outline: 2px solid rgba(0, 122, 204, 0.3) !important;
      outline-offset: 2px !important;
    }

    @media (max-width: 768px) {
      /* Exclude radio buttons from general appearance reset */
      input:not([type='radio']), textarea, select {
        font-size: 16px !important;
        touch-action: manipulation;
        transform: translateZ(0);
        -webkit-user-select: text;
        -webkit-appearance: none;
        border-radius: 0;
      }
      
      /* Mobile radio buttons - large and clearly visible */
      input[type='radio'] {
        width: 24px !important;
        height: 24px !important;
        min-width: 24px !important;
        min-height: 24px !important;
        appearance: none !important;
        -webkit-appearance: none !important;
        -moz-appearance: none !important;
        margin: 0 12px 0 0 !important;
        flex-shrink: 0 !important;
        position: relative !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: inline-block !important;
        border: 3px solid #007acc !important;
        border-radius: 50% !important;
        background: white !important;
        cursor: pointer !important;
        transform: none !important;
      }
      
      /* Radio button checked state */
      input[type='radio']:checked {
        background: #007acc !important;
        border-color: #007acc !important;
        box-shadow: inset 0 0 0 4px white !important;
      }
      
      /* Radio button focus state */
      input[type='radio']:focus {
        outline: 3px solid rgba(0, 122, 204, 0.3) !important;
        outline-offset: 2px !important;
      }
      
      /* Radio labels - better mobile styling (no :has() for compatibility) */
      .radio-label {
        display: flex !important;
        align-items: center !important;
        gap: 12px !important;
        cursor: pointer !important;
        padding: 12px 8px !important;
        font-size: 16px !important;
        line-height: 1.4 !important;
        border: 1px solid transparent !important;
        border-radius: 6px !important;
        margin: 4px 0 !important;
        transition: all 0.2s ease !important;
      }
      
      /* Radio label hover/active states */
      .radio-label:hover {
        background-color: rgba(0, 122, 204, 0.05) !important;
        border-color: rgba(0, 122, 204, 0.2) !important;
      }
      
      .radio-label.checked {
        background-color: rgba(0, 122, 204, 0.1) !important;
        border-color: rgba(0, 122, 204, 0.3) !important;
      }
      
      input[type='number'] {
        -moz-appearance: textfield;
      }
      
      input[type='number']::-webkit-outer-spin-button,
      input[type='number']::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      
      /* Prevent zoom on focus */
      input:focus, textarea:focus, select:focus {
        font-size: 16px !important;
        outline: none;
        transform: translateZ(0);
      }
    }
  `;
  
  // Create Lightning invoice
  const createInvoice = async () => {
    setIsCreatingInvoice(true);
    try {
      // Prepare submission data
      const submissionData = {
        expert_name: formData.expert_name,
        twitter_handle: formData.twitter_handle,
        nostr_npub: formData.nostr_npub,
        prediction_type: formData.prediction_type,
        target_time: formData.target_time,
        submitter_email: formData.submitter_email,
        notes: formData.notes,
        source_url: formData.source_url,
        asset_type: currentAsset, // Add asset type to detect MSTR vs BTC submissions
        profile_image_url: extractionResult?.data?.profile_image_url || null, // Include profile image URL
        price_prediction:
          formData.prediction_type === 'target'
            ? `$${Number(formData.price_target).toLocaleString()}`
            : formData.prediction_type === 'range'
            ? `$${Number(formData.price_min).toLocaleString()} - $${Number(formData.price_max).toLocaleString()}`
            : formData.prediction_type === 'pricetimerange'
            ? `$${Number(formData.price_min).toLocaleString()}`
            : formData.prediction_type === 'top'
            ? `$${Number(formData.price_target).toLocaleString()}`
            : formData.prediction_type === 'bottom'
            ? `$${Number(formData.price_target).toLocaleString()}`
            : 'No price specified',
        price_min:
          ['range', 'pricetimerange', 'bottom'].includes(formData.prediction_type) && formData.price_min
            ? Number(formData.price_min)
            : formData.prediction_type === 'bottom' && formData.price_target
            ? Number(formData.price_target)
            : null,
        price_max:
          formData.prediction_type === 'range' && formData.price_max
            ? Number(formData.price_max)
            : formData.prediction_type === 'pricetimerange' && formData.price_min
            ? Number(formData.price_min)
            : formData.prediction_type === 'top' && formData.price_target
            ? Number(formData.price_target)
            : null
      };

      const response = await fetch('/api/payments/create-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: paymentAmount,
          submissionData: submissionData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create invoice');
      }

      const invoiceData = await response.json();
      
      // Generate QR code for the payment request
      let qrDataUrl = '';
      if (invoiceData.invoice?.payment_request) {
        try {
          qrDataUrl = await QRCode.toDataURL(invoiceData.invoice.payment_request, {
            width: 256,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });
        } catch (error) {
          console.error('Error generating QR code:', error);
        }
      }
      
      setPaymentData({
        amount: paymentAmount,
        bolt11: invoiceData.invoice.payment_request,
        payment_hash: invoiceData.invoice.payment_hash,
        checking_id: invoiceData.invoice.checking_id,
      });
      setQrCodeDataUrl(qrDataUrl);

      // Debug: Log the invoice data structure

      // Start monitoring payment
      monitorPayment(invoiceData.invoice.checking_id);
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Failed to create payment invoice. Please try again.');
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  // Monitor payment status
  const monitorPayment = async (checkingId: string) => {
    
    const checkPayment = async () => {
      try {
        const response = await fetch(`/api/payments/check-payment/${checkingId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.paid) {
            setPaymentStatus('paid');
            // Prediction is automatically submitted by backend after payment
            setTimeout(() => {
              // Reset form and show success
              setFormData({
                expert_name: '',
                twitter_handle: '',
                nostr_npub: '',
                prediction_type: 'target',
                price_target: '',
                target_time: getDefaultPredictionDate(),
                source_url: '',
                notes: '',
                submitter_email: ''
              });
              setShowPaymentModal(false);
              setSubmissionSuccess(true);
            }, 2000);
            return true;
          }
        }
        return false;
      } catch (error) {
        console.error('Error checking payment:', error);
        return false;
      }
    };

    // Check payment status every 2 seconds for 10 minutes
    const maxAttempts = 300; // 10 minutes
    let attempts = 0;

    const interval = setInterval(async () => {
      attempts++;
      const paid = await checkPayment();
      
      if (paid || attempts >= maxAttempts) {
        clearInterval(interval);
        if (attempts >= maxAttempts && paymentStatus !== 'paid') {
          setPaymentStatus('expired');
        }
      }
    }, 2000);
  };

  // Handle payment modal close
  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentData(null);
    setPaymentStatus('pending');
    setPaymentAmount(1); // Reset to 1 sat, not 1000
    setQrCodeDataUrl('');
  };
  
  // Payment Modal Component
  const PaymentModal = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: theme.surface,
        borderRadius: '12px',
        padding: '30px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        border: `1px solid ${theme.border}`,
      }}>
        <h2 style={{ 
          color: theme.text, 
          marginBottom: '20px',
          textAlign: 'center',
        }}>
          ⚡ Pay What You Think Is Fair
        </h2>
        
        {!paymentData ? (
          // Payment amount selection
          <div>
            <p style={{ color: theme.subText, marginBottom: '20px', textAlign: 'center' }}>
              Time is our most valuable resource. We'll invest ours to check, verify and maintain your submission. You decide what that service is worth to you.
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ ...formStyles.label, color: theme.text }}>
                Amount (satoshis):
              </label>
              <input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(Number(e.target.value))}
                min={1}
                max={100000}
                style={{
                  ...formStyles.input,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                }}
                placeholder="1"
              />
              <div style={{ 
                fontSize: '12px', 
                color: theme.subText, 
                marginTop: '5px',
                textAlign: 'center'
              }}>
                ≈ ${(paymentAmount * 0.001).toFixed(2)} USD
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              {[1, 21, 500, 1000, 2100].map(amount => (
                <button
                  key={amount}
                  onClick={() => setPaymentAmount(amount)}
                  onTouchEnd={(e) => {
                    // Prevent double-tap and ensure single execution
                    e.preventDefault();
                    setPaymentAmount(amount);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 8px', // Increased padding for better touch targets
                    minHeight: '44px', // iOS/Android recommended minimum touch target
                    border: paymentAmount === amount ? `2px solid ${theme.primary}` : `1px solid ${theme.border}`,
                    backgroundColor: paymentAmount === amount ? theme.primary + '20' : theme.surface,
                    color: theme.text,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    // Mobile-specific improvements
                    touchAction: 'manipulation', // Prevent zoom on tap
                    userSelect: 'none', // Prevent text selection
                    WebkitTapHighlightColor: 'transparent', // Remove tap highlight on iOS
                    transition: 'background-color 0.15s ease', // Smooth transitions
                  }}
                >
                  {amount} sats
                </button>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleClosePaymentModal}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={createInvoice}
                disabled={isCreatingInvoice}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  backgroundColor: theme.primary,
                  color: 'white',
                  borderRadius: '6px',
                  cursor: isCreatingInvoice ? 'not-allowed' : 'pointer',
                  opacity: isCreatingInvoice ? 0.7 : 1,
                }}
              >
                {isCreatingInvoice ? 'Creating...' : 'Create Invoice'}
              </button>
            </div>
          </div>
        ) : (
          // Payment QR code and status
          <div style={{ textAlign: 'center' }}>
            {paymentStatus === 'pending' && (
              <>
                {qrCodeDataUrl && (
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '20px'
                  }}>
                    <img 
                      src={qrCodeDataUrl} 
                      alt="Lightning Payment QR Code"
                      style={{
                        maxWidth: '256px',
                        width: '100%',
                        height: 'auto',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px'
                      }}
                    />
                  </div>
                )}
                
                {/* Lightning Invoice Container with Copy Button */}
                <div style={{
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  marginBottom: '20px',
                }}>
                  {/* Invoice Text */}
                  <div 
                    id="lightning-invoice-text"
                    style={{
                      backgroundColor: '#f8f9fa',
                      padding: '20px',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      color: '#000',
                      borderBottom: '1px solid #e9ecef',
                    }}
                  >
                    {paymentData.bolt11}
                  </div>
                  
                  {/* Copy Button */}
                  <button
                    onClick={async (e) => {
                      e.preventDefault();
                      
                      // Show copying state
                      const button = e.currentTarget;
                      const originalText = button.innerHTML;
                      button.innerHTML = '📋 Copying...';
                      button.disabled = true;
                      
                      let copied = false;
                      
                      try {
                        // Method 1: Modern clipboard API
                        if (navigator.clipboard && window.isSecureContext) {
                          await navigator.clipboard.writeText(paymentData.bolt11);
                          copied = true;
                        }
                      } catch (err) {
                      }
                      
                      if (!copied) {
                        try {
                          // Method 2: Create temporary textarea (works in more browsers)
                          const textArea = document.createElement('textarea');
                          textArea.value = paymentData.bolt11;
                          textArea.style.position = 'fixed';
                          textArea.style.left = '-999999px';
                          textArea.style.top = '-999999px';
                          document.body.appendChild(textArea);
                          textArea.focus();
                          textArea.select();
                          
                          // For mobile devices
                          textArea.setSelectionRange(0, 99999);
                          
                          copied = document.execCommand('copy');
                          document.body.removeChild(textArea);
                        } catch (err) {
                          console.error('Fallback copy method failed:', err);
                        }
                      }
                      
                      if (!copied) {
                        // Method 3: Select text in the invoice div
                        try {
                          const invoiceElement = document.getElementById('lightning-invoice-text');
                          if (invoiceElement) {
                            const selection = window.getSelection();
                            const range = document.createRange();
                            range.selectNodeContents(invoiceElement);
                            selection?.removeAllRanges();
                            selection?.addRange(range);
                            copied = document.execCommand('copy');
                            selection?.removeAllRanges();
                          }
                        } catch (err) {
                          console.error('Text selection copy failed:', err);
                        }
                      }
                      
                      // Show result
                      if (copied) {
                        button.innerHTML = '✅ Copied!';
                        button.style.backgroundColor = '#4CAF50';
                        button.style.color = 'white';
                        
                        // Show success toast
                        const toast = document.createElement('div');
                        toast.innerHTML = '✅ Lightning invoice copied to clipboard!';
                        toast.style.cssText = `
                          position: fixed;
                          top: 50%;
                          left: 50%;
                          transform: translate(-50%, -50%);
                          background: rgba(76, 175, 80, 0.95);
                          color: white;
                          padding: 16px 24px;
                          border-radius: 12px;
                          font-weight: 600;
                          font-size: 14px;
                          z-index: 10000;
                          pointer-events: none;
                          box-shadow: 0 8px 24px rgba(0,0,0,0.2);
                          animation: fadeInOut 3s ease-in-out;
                        `;
                        
                        // Add fade animation
                        const style = document.createElement('style');
                        style.textContent = `
                          @keyframes fadeInOut {
                            0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
                            20% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                            80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                            100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
                          }
                        `;
                        document.head.appendChild(style);
                        document.body.appendChild(toast);
                        
                        setTimeout(() => {
                          toast.remove();
                          style.remove();
                        }, 3000);
                        
                        setTimeout(() => {
                          button.innerHTML = originalText;
                          button.style.backgroundColor = '';
                          button.style.color = '';
                          button.disabled = false;
                        }, 2000);
                      } else {
                        button.innerHTML = '❌ Copy failed';
                        button.style.backgroundColor = '#f44336';
                        button.style.color = 'white';
                        
                        setTimeout(() => {
                          button.innerHTML = originalText;
                          button.style.backgroundColor = '';
                          button.style.color = '';
                          button.disabled = false;
                        }, 2000);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    }}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor = '#0056b3';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor = '#007bff';
                      }
                    }}
                    onTouchStart={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor = '#0056b3';
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (!e.currentTarget.disabled) {
                        setTimeout(() => {
                          e.currentTarget.style.backgroundColor = '#007bff';
                        }, 150);
                      }
                    }}
                  >
                    📋 Copy Lightning Invoice
                  </button>
                </div>
                
                <p style={{ color: theme.text, marginBottom: '15px' }}>
                  Scan the QR code with your Lightning wallet or use the <strong>Copy button</strong> above to copy the payment request.
                </p>
                
                <div style={{ 
                  color: theme.subText, 
                  fontSize: '14px',
                  marginBottom: '20px'
                }}>
                  Amount: {paymentData.amount} sats (≈ ${(paymentData.amount * 0.001).toFixed(2)} USD)
                </div>

                <div style={{ 
                  color: theme.primary, 
                  fontSize: '14px',
                  marginBottom: '15px'
                }}>
                  🔄 Waiting for payment...
                </div>
              </>
            )}
            
            {paymentStatus === 'paid' && (
              <>
                <div style={{ 
                  color: '#28a745', 
                  fontSize: '18px',
                  marginBottom: '15px'
                }}>
                  ✅ Payment Received!
                </div>
                <p style={{ color: theme.text }}>
                  {isSubmitting ? 'Submitting your prediction...' : 'Your prediction has been submitted successfully!'}
                </p>
              </>
            )}
            
            {paymentStatus === 'expired' && (
              <>
                <div style={{ 
                  color: '#dc3545', 
                  fontSize: '18px',
                  marginBottom: '15px'
                }}>
                  ⏰ Payment Expired
                </div>
                <p style={{ color: theme.text, marginBottom: '20px' }}>
                  The payment request has expired. Please try again.
                </p>
                <button
                  onClick={handleClosePaymentModal}
                  style={{
                    padding: '12px 20px',
                    border: 'none',
                    backgroundColor: theme.primary,
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Try Again
                </button>
              </>
            )}
            
            {paymentStatus === 'pending' && (
              <button
                onClick={handleClosePaymentModal}
                style={{
                  padding: '10px 20px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  marginTop: '10px',
                }}
              >
                Cancel
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
  
  return (
    <>
      <style>{radioAndMobileStyles}</style>
      {showPaymentModal && <PaymentModal />}
      <div style={{
        ...formStyles.container,
        backgroundColor: theme.surface,
        color: theme.text,
        border: `1px solid ${theme.border}`,
        // Dynamic sizing based on form state
        maxWidth: (isFormExpanded || hasSuccessfulExtraction) ? '500px' : '340px',
        minHeight: (isFormExpanded || hasSuccessfulExtraction) ? '400px' : '200px',
        transition: 'all 0.3s ease'
      }}>

        
        <form onSubmit={handleSubmit} autoComplete="off" style={{ 
          WebkitTapHighlightColor: 'transparent',
          transform: 'translateZ(0)', // Force hardware acceleration
          WebkitTransform: 'translateZ(0)'
        }}>
          <h3 style={{ color: theme.primary, marginBottom: '8px', marginTop: '0px', fontSize: '14px' }}>
            Submit Prediction
          </h3>
          <p style={{ color: theme.text, marginBottom: '12px', fontSize: '12px', lineHeight: '1.4' }}>
            Found a {currentAssetInfo.name} prediction? Submit it here.
          </p>
          
          {/* Unified Input: Tweet URL or Text */}
          <div style={formStyles.formGroup}>
            <label 
              htmlFor="prediction_input" 
              style={{ ...formStyles.label, color: theme.text }}
            >
              🤖 Smart Extraction
            </label>
            <textarea 
              id="prediction_input"
              name="prediction_input"
              value={predictionInput}
              onChange={handlePredictionInputChange}
              placeholder="Paste X URL here for automatic extraction"
              rows={3}
              style={{ 
                ...formStyles.input,
                backgroundColor: theme.background,
                color: theme.text,
                borderColor: theme.border,
                fontSize: '14px',
                touchAction: 'manipulation',
                minHeight: '80px',
                resize: 'vertical'
              }}
              autoComplete="off"
              autoCapitalize="sentences"
              autoCorrect="off"
              spellCheck="true"
            />
            <small style={{ ...formStyles.small, color: theme.subText }}>
              If it fails, we are probably hitting rate limits. Try again in 15 minutes.
            </small>
            {predictionInputError && <div style={formStyles.error}>{predictionInputError}</div>}
            
            {/* Action Buttons Row */}
            <div style={{ 
              marginTop: '10px', 
              display: 'flex', 
              gap: '10px', 
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <button 
                type="button"
                onClick={handleExtractPreview}
                disabled={!predictionInput.trim() || isExtracting}
                style={{
                  backgroundColor: theme.primary,
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: predictionInput.trim() && !isExtracting ? 'pointer' : 'not-allowed',
                  opacity: predictionInput.trim() && !isExtracting ? 1 : 0.6,
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                {isExtracting ? '🤖 Analyzing...' : '🚀 Extract'}
              </button>

              <button 
                type="button"
                onClick={() => setIsFormExpanded(true)}
                style={{
                  backgroundColor: theme.border,
                  color: theme.text,
                  border: `1px solid ${theme.primary}`,
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                📝 Manual
              </button>

              {/* Quick Submit Button - Show when extraction is successful */}
              {hasSuccessfulExtraction && extractionResult?.success && (
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: theme.primary,
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1,
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  {isSubmitting ? 'Submitting...' : `✅ Submit`}
                </button>
              )}
            </div>
            
            {/* Preview Result */}
            {extractionResult && (
              <div style={{
                marginTop: '15px',
                padding: '15px',
                backgroundColor: extractionResult.success ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                border: `1px solid ${extractionResult.success ? 'rgba(40, 167, 69, 0.3)' : 'rgba(220, 53, 69, 0.3)'}`,
                borderRadius: '4px'
              }}>
                {extractionResult.success ? (
                  <div>
                    <div style={{ color: '#28a745', fontWeight: 'bold', marginBottom: '10px' }}>
                      ✅ Prediction Extracted Successfully!
                    </div>
                    <div style={{ color: theme.text, fontSize: '14px' }}>
                      <strong>Expert:</strong> {extractionResult.data.expert_name}<br/>
                      <strong>Prediction:</strong> {extractionResult.data.price_prediction}<br/>
                      <strong>Type:</strong> {extractionResult.data.prediction_type}<br/>
                      {extractionResult.data.target_time && <><strong>Target:</strong> {extractionResult.data.target_time}<br/></>}
                      <div style={{ marginTop: '8px', fontSize: '12px', color: theme.subText }}>
                        🤖 Powered by Grok AI • Form will auto-fill below
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ color: '#dc3545', fontWeight: 'bold', marginBottom: '10px' }}>
                      ❌ {extractionResult.error}
                    </div>
                    {extractionResult.requiresManualInput && (
                      <div style={{ color: theme.subText, fontSize: '14px' }}>
                        💡 Try pasting the tweet text directly instead of the URL
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          

          
          {/* Form Fields - Show when expanded or after successful extraction */}
          {(isFormExpanded || hasSuccessfulExtraction) && (
            <>
              {/* Expert Name */}
          <div style={formStyles.formGroup}>
            <label 
              htmlFor="expert_name" 
              style={{ ...formStyles.label, color: theme.text }}
            >
              Expert/Influencer Name *
            </label>
            <input 
              id="expert_name"
              name="expert_name"
              type="text"
              value={formData.expert_name}
              onChange={handleChange}
              placeholder="e.g. Michael Saylor, Cathie Wood"
              required={!hasSuccessfulExtraction}
              style={{ 
                ...formStyles.input,
                backgroundColor: theme.background,
                color: theme.text,
                borderColor: theme.border,
                fontSize: '14px',
                touchAction: 'manipulation'
              }}
              inputMode="text"
              autoComplete="off"
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck="false"
            />
            <small style={{ ...formStyles.small, color: theme.subText }}>
              The name of the person who made this {currentAssetInfo.name} price prediction
            </small>
            {formErrors.expert_name && <div style={formStyles.error}>{formErrors.expert_name}</div>}
          </div>
          
          {/* Twitter Handle */}
          <div style={formStyles.formGroup}>
            <label 
              htmlFor="twitter_handle" 
              style={{ ...formStyles.label, color: theme.text }}
            >
              Twitter/X Handle (Optional)
            </label>
            <input 
              id="twitter_handle"
              name="twitter_handle"
              type="text"
              value={formData.twitter_handle}
              onChange={handleChange}
              placeholder="e.g. @michaelsaylor"
              style={{ 
                ...formStyles.input,
                backgroundColor: theme.background,
                color: theme.text,
                borderColor: theme.border,
                fontSize: '14px',
                touchAction: 'manipulation'
              }}
              inputMode="text"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
            />
            <small style={{ ...formStyles.small, color: theme.subText }}>
              The Twitter/X handle of the person who made this prediction
            </small>
          </div>

          {/* Nostr NPub */}
          <div style={formStyles.formGroup}>
            <label 
              htmlFor="nostr_npub" 
              style={{ ...formStyles.label, color: theme.text }}
            >
              Nostr NPub (Optional)
            </label>
            <input 
              id="nostr_npub"
              name="nostr_npub"
              type="text"
              value={formData.nostr_npub}
              onChange={handleChange}
              placeholder="e.g. npub1..."
              style={{ 
                ...formStyles.input,
                backgroundColor: theme.background,
                color: theme.text,
                borderColor: theme.border,
                fontSize: '14px',
                touchAction: 'manipulation'
              }}
              inputMode="text"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
            />
            <small style={{ ...formStyles.small, color: theme.subText }}>
              The npub of the person who made this prediction
            </small>
          </div>
          
          {/* Prediction Type */}
          <div style={formStyles.formGroup}>
            <label style={{ ...formStyles.label, color: theme.text }}>
              Prediction Type *
            </label>
            <div style={formStyles.radioGroup}>
              {[
                { value: 'top', label: `Price Ceiling/Top (e.g. "${currentAssetInfo.symbol} will never go above ${currentAssetInfo.examples.target}")` },
                { value: 'bottom', label: `Price Floor/Bottom (e.g. "${currentAssetInfo.symbol} will never go below $50,000")` },
                { value: 'target', label: `Exact Price Target (e.g. "${currentAssetInfo.examples.target}")` },
                { value: 'range', label: `Price Range (e.g. "${currentAssetInfo.examples.range}")` },
                { value: 'pricetimerange', label: `Minimum Price By Date (e.g. "${currentAssetInfo.symbol} will be above ${currentAssetInfo.examples.target} by 2025")` },
                { value: 'sentiment', label: `💭 Sentiment Tweet (e.g. "Bitcoin is going up!", "We're going down", general market sentiment)` }
              ].map(option => (
                <label 
                  key={option.value} 
                  className={`radio-label ${formData.prediction_type === option.value ? 'checked' : ''}`}
                  style={{ 
                    ...formStyles.radioLabel, 
                    color: theme.text,
                    backgroundColor: formData.prediction_type === option.value ? 'rgba(0, 122, 204, 0.1)' : 'transparent',
                    borderColor: formData.prediction_type === option.value ? 'rgba(0, 122, 204, 0.3)' : 'transparent'
                  }}
                >
                  <input 
                    type="radio"
                    name="prediction_type"
                    value={option.value}
                    checked={formData.prediction_type === option.value}
                    onChange={handleRadioChange}
                    style={{
                      ...formStyles.radioInput,
                      backgroundColor: formData.prediction_type === option.value ? '#007acc' : 'white',
                      borderColor: '#007acc',
                      boxShadow: formData.prediction_type === option.value ? 'inset 0 0 0 3px white' : 'none'
                    } as React.CSSProperties}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <small style={{ ...formStyles.small, color: theme.subText }}>
              Select the type of price prediction that was made for {currentAssetInfo.name}
            </small>
          </div>
          
          {/* Conditional price fields - use asset-appropriate placeholders */}
          {formData.prediction_type === 'target' && (
            <div style={formStyles.formGroup}>
              <label htmlFor="price_target" style={{ ...formStyles.label, color: theme.text }}>
                Price Target (USD) *
              </label>
              <input 
                id="price_target"
                name="price_target"
                type="number"
                value={formData.price_target}
                onChange={handleChange}
                placeholder={currentAsset === 'mstr' ? '500' : currentAsset === 'naka' ? '10' : '100000'}
                required={!hasSuccessfulExtraction}
                style={{ 
                  ...formStyles.input,
                  backgroundColor: theme.background,
                  color: theme.text,
                  borderColor: theme.border,
                  fontSize: '14px',
                  touchAction: 'manipulation'
                }}
                inputMode="decimal"
                pattern="[0-9]*"
                step="any"
                autoComplete="off"
              />
              <small style={{ ...formStyles.small, color: theme.subText }}>
                The exact price target mentioned for {currentAssetInfo.name} (in USD, without $ symbol)
              </small>
              {formErrors.price_target && <div style={formStyles.error}>{formErrors.price_target}</div>}
            </div>
          )}
          
          {formData.prediction_type === 'range' && (
            <div style={formStyles.formRow}>
              <div style={formStyles.formGroup}>
                <label htmlFor="price_min" style={{ ...formStyles.label, color: theme.text }}>
                  Minimum Price (USD) *
                </label>
                <input 
                  id="price_min"
                  name="price_min"
                  type="number"
                  value={formData.price_min}
                  onChange={handleChange}
                  placeholder="80000"
                  required={!hasSuccessfulExtraction}
                  style={{ 
                    ...formStyles.input,
                    backgroundColor: theme.background,
                    color: theme.text,
                    borderColor: theme.border,
                    fontSize: '14px',
                    touchAction: 'manipulation'
                  }}
                  inputMode="decimal"
                  pattern="[0-9]*"
                  step="any"
                  autoComplete="off"
                />
                {formErrors.price_min && <div style={formStyles.error}>{formErrors.price_min}</div>}
              </div>
              <div style={formStyles.formGroup}>
                <label htmlFor="price_max" style={{ ...formStyles.label, color: theme.text }}>
                  Maximum Price (USD) *
                </label>
                <input 
                  id="price_max"
                  name="price_max"
                  type="number"
                  value={formData.price_max}
                  onChange={handleChange}
                  placeholder="120000"
                  required={!hasSuccessfulExtraction}
                  style={{ 
                    ...formStyles.input,
                    backgroundColor: theme.background,
                    color: theme.text,
                    borderColor: theme.border,
                    fontSize: '14px',
                    touchAction: 'manipulation'
                  }}
                  inputMode="decimal"
                  pattern="[0-9]*"
                  step="any"
                  autoComplete="off"
                />
                {formErrors.price_max && <div style={formStyles.error}>{formErrors.price_max}</div>}
              </div>
            </div>
          )}
          
          {formData.prediction_type === 'pricetimerange' && (
            <div style={formStyles.formGroup}>
              <label htmlFor="price_min" style={{ ...formStyles.label, color: theme.text }}>
                Minimum Price (USD) *
              </label>
              <input 
                id="price_min"
                name="price_min"
                type="number"
                value={formData.price_min}
                onChange={handleChange}
                placeholder="100000"
                required={!hasSuccessfulExtraction}
                style={{ 
                  ...formStyles.input,
                  backgroundColor: theme.background,
                  color: theme.text,
                  borderColor: theme.border,
                  fontSize: '14px',
                  touchAction: 'manipulation'
                }}
                inputMode="decimal"
                pattern="[0-9]*"
                step="any"
                autoComplete="off"
              />
              <small style={{ ...formStyles.small, color: theme.subText }}>
                The minimum price Bitcoin will reach by the target date
              </small>
              {formErrors.price_min && <div style={formStyles.error}>{formErrors.price_min}</div>}
            </div>
          )}
          
          {formData.prediction_type === 'top' && (
            <div style={formStyles.formGroup}>
              <label htmlFor="price_target" style={{ ...formStyles.label, color: theme.text }}>
                Maximum Price/Ceiling (USD) *
              </label>
              <input 
                id="price_target"
                name="price_target"
                type="number"
                value={formData.price_target}
                onChange={handleChange}
                placeholder={currentAsset === 'mstr' ? '1000' : currentAsset === 'naka' ? '100' : '200000'}
                required={!hasSuccessfulExtraction}
                style={{ 
                  ...formStyles.input,
                  backgroundColor: theme.background,
                  color: theme.text,
                  borderColor: theme.border,
                  fontSize: '14px',
                  touchAction: 'manipulation'
                }}
                inputMode="decimal"
                pattern="[0-9]*"
                step="any"
                autoComplete="off"
              />
              <small style={{ ...formStyles.small, color: theme.subText }}>
                The maximum price {currentAssetInfo.name} will never exceed (price ceiling)
              </small>
              {formErrors.price_target && <div style={formStyles.error}>{formErrors.price_target}</div>}
            </div>
          )}
          
          {formData.prediction_type === 'bottom' && (
            <div style={formStyles.formGroup}>
              <label htmlFor="price_target" style={{ ...formStyles.label, color: theme.text }}>
                Minimum Price/Floor (USD) *
              </label>
              <input 
                id="price_target"
                name="price_target"
                type="number"
                value={formData.price_target}
                onChange={handleChange}
                placeholder={currentAsset === 'mstr' ? '100' : currentAsset === 'naka' ? '1' : '50000'}
                required={!hasSuccessfulExtraction}
                style={{ 
                  ...formStyles.input,
                  backgroundColor: theme.background,
                  color: theme.text,
                  borderColor: theme.border,
                  fontSize: '14px',
                  touchAction: 'manipulation'
                }}
                inputMode="decimal"
                pattern="[0-9]*"
                step="any"
                autoComplete="off"
              />
              <small style={{ ...formStyles.small, color: theme.subText }}>
                The minimum price {currentAssetInfo.name} will never go below (price floor)
              </small>
              {formErrors.price_target && <div style={formStyles.error}>{formErrors.price_target}</div>}
            </div>
          )}

          {/* Sentiment Tweet Fields */}
          {formData.prediction_type === 'sentiment' && (
            <div style={formStyles.formGroup}>
              <div style={{
                padding: '16px',
                backgroundColor: `${theme.primary}10`,
                borderRadius: '8px',
                border: `1px solid ${theme.primary}30`,
                marginBottom: '16px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: theme.primary, fontSize: '14px' }}>
                  💭 Sentiment Tweet
                </h4>
                <p style={{ margin: '0', color: theme.text, fontSize: '14px', lineHeight: '1.5' }}>
                  You're submitting a sentiment tweet! This is for general market sentiment like "Bitcoin is going up!", 
                  "We're going down", or other directional/emotional commentary without specific price targets. 
                  The tweet will be placed on the chart at the timestamp when it was made.
                </p>
              </div>
              <label htmlFor="sentiment_text" style={{ ...formStyles.label, color: theme.text }}>
                Sentiment Text/Quote *
              </label>
              <textarea 
                id="sentiment_text"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="e.g. Bitcoin is going to the moon!, This is it we're going down, Bullish AF on BTC"
                required={!hasSuccessfulExtraction}
                rows={3}
                style={{ 
                  ...formStyles.input,
                  backgroundColor: theme.background,
                  color: theme.text,
                  borderColor: theme.border,
                  fontSize: '14px',
                  touchAction: 'manipulation',
                  resize: 'vertical'
                }}
                autoComplete="off"
                autoCapitalize="sentences"
                autoCorrect="on"
                spellCheck="true"
              />
              <small style={{ ...formStyles.small, color: theme.subText }}>
                The exact sentiment text, quote, or tweet content. This will be displayed on the chart timeline.
              </small>
              {formErrors.notes && <div style={formStyles.error}>{formErrors.notes}</div>}
            </div>
          )}
          
          {/* Target Date */}
          <div style={formStyles.formGroup}>
            <label htmlFor="target_time" style={{ ...formStyles.label, color: theme.text }}>
              {formData.prediction_type === 'sentiment' ? 'Tweet Date *' : 'Prediction Target Date *'}
            </label>
            <input 
              id="target_time"
              name="target_time"
              type="date"
              value={formData.target_time}
              onChange={handleChange}
              required={!hasSuccessfulExtraction}
              style={{ 
                ...formStyles.input,
                backgroundColor: theme.background,
                color: theme.text,
                borderColor: theme.border,
                fontSize: '14px',
                touchAction: 'manipulation'
              }}
              autoComplete="off"
            />
            <small style={{ ...formStyles.small, color: theme.subText }}>
              {formData.prediction_type === 'sentiment' 
                ? 'When was this sentiment expressed? This will position the tweet on the chart timeline.'
                : `When did they say this ${currentAssetInfo.name} price would be reached? (Default: 3 months from now for better chart visibility)`
              }
            </small>
            {formErrors.target_time && <div style={formStyles.error}>{formErrors.target_time}</div>}
          </div>
          
          {/* Source URL */}
          <div style={formStyles.formGroup}>
            <label htmlFor="source_url" style={{ ...formStyles.label, color: theme.text }}>
              Source Link
            </label>
            <input 
              id="source_url"
              name="source_url"
              type="text"
              value={formData.source_url}
              onChange={handleChange}
              placeholder="twitter.com/example/status/123456789"
              style={{ 
                ...formStyles.input,
                backgroundColor: theme.background,
                color: theme.text,
                borderColor: theme.border,
                fontSize: '14px',
                touchAction: 'manipulation'
              }}
              inputMode="url"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
            />
            <small style={{ ...formStyles.small, color: theme.subText }}>
              URL to tweet, article, video, or other source where this prediction was made. This helps us verify the details!
            </small>
            {formErrors.source_url && <div style={formStyles.error}>{formErrors.source_url}</div>}
          </div>
          
          {/* Notes */}
          <div style={formStyles.formGroup}>
            <label htmlFor="notes" style={{ ...formStyles.label, color: theme.text }}>
              Additional Context
            </label>
            <textarea 
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              readOnly={hasSuccessfulExtraction && extractionResult?.data?.source_tweet_text}
              placeholder={hasSuccessfulExtraction && extractionResult?.data?.source_tweet_text 
                ? "Tweet content (extracted automatically)"
                : "Any additional details or context around this prediction (e.g., 'said in Q4 2024', 'mentioned during podcast', etc.)"}
              rows={3}
              style={{ 
                ...formStyles.input,
                backgroundColor: hasSuccessfulExtraction && extractionResult?.data?.source_tweet_text 
                  ? theme.border  // Grayed out background for read-only
                  : theme.background,
                color: theme.text,
                borderColor: theme.border,
                fontSize: '14px',
                touchAction: 'manipulation',
                cursor: hasSuccessfulExtraction && extractionResult?.data?.source_tweet_text 
                  ? 'not-allowed' 
                  : 'text'
              }}
              autoComplete="off"
              autoCapitalize="sentences"
              autoCorrect="on"
              spellCheck="true"
            />
            <small style={{ ...formStyles.small, color: theme.subText }}>
              Any additional details, quotes, or context. If you're unsure about dates or details, just describe what you remember - we'll research and verify during review.
            </small>
          </div>
          
          {/* Submitter Email */}
          <div style={formStyles.formGroup}>
            <label htmlFor="submitter_email" style={{ ...formStyles.label, color: theme.text }}>
              Your Email (optional)
            </label>
            <input 
              id="submitter_email"
              name="submitter_email"
              type="email"
              value={formData.submitter_email}
              onChange={handleChange}
              placeholder="your@email.com"
              style={{ 
                ...formStyles.input,
                backgroundColor: theme.background,
                color: theme.text,
                borderColor: theme.border,
                fontSize: '14px',
                touchAction: 'manipulation'
              }}
              inputMode="email"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
            />
            <small style={{ ...formStyles.small, color: theme.subText }}>
              We'll only use this to contact you if we need clarification. You can also reach out to us on Twitter/X or Nostr if you have questions!
            </small>
            {formErrors.submitter_email && <div style={formStyles.error}>{formErrors.submitter_email}</div>}
          </div>

          {/* Preview Manual Input Button - Show only when form is expanded and not from extraction */}
          {isFormExpanded && !hasSuccessfulExtraction && (
            <div style={{ textAlign: 'center', margin: '20px 0' }}>
              <button 
                type="button"
                onClick={() => {
                  updatePreviewFromForm();
                  // You could add visual feedback here
                }}
                disabled={!formData.expert_name || !formData.target_time || (formData.prediction_type !== 'sentiment' && !formData.price_target && !formData.price_min && !formData.price_max)}
                style={{
                  backgroundColor: theme.primary,
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '4px',
                  cursor: (!formData.expert_name || !formData.target_time || (formData.prediction_type !== 'sentiment' && !formData.price_target && !formData.price_min && !formData.price_max)) ? 'not-allowed' : 'pointer',
                  opacity: (!formData.expert_name || !formData.target_time || (formData.prediction_type !== 'sentiment' && !formData.price_target && !formData.price_min && !formData.price_max)) ? 0.6 : 1,
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                👁️ Preview Manual Input
              </button>
              <div style={{ fontSize: '12px', color: theme.subText, marginTop: '8px' }}>
                Preview your prediction on the chart before submitting
              </div>
            </div>
          )}
          </>
          )}
          
          <button 
            type="submit"
            disabled={isSubmitting}
            style={{ 
              ...formStyles.submitButton,
              backgroundColor: theme.primary,
              color: 'white',
              opacity: isSubmitting ? 0.7 : 1
            }}
          >
            {isSubmitting ? 'Submitting...' : `Submit`}
          </button>
        </form>
      </div>
      
      {showPaymentModal && <PaymentModal />}
    </>
  );
};

export default SubmitPredictionForm;